// components/BusinessPlan.tsx
import React, { useCallback, useEffect, useState } from "react";
import { Formik } from "formik";
import {
  DollarSign,
  Crown,
  Zap,
  Star,
  Check,
  X,
  Calendar,
  Infinity,
  Shield,
  ArrowRight,
  CreditCard,
  Wallet,
  Sparkles,
  TrendingUp,
  Target,
  AlertTriangle,
} from "lucide-react";
import { useModal } from "@context/ModalContext";
import CommissionSettings from "./commision-setting";
import { useSelector } from "react-redux";
import { RootState } from "@store/index";
import { Subscription, Store as StoreApi } from "@apis/index";
import { toast } from "react-toastify";
import * as S from "./style";
import { useGeneralSettings } from "@hooks/useGeneralSettings";
import { Helper } from "@utils/index";

// Helper functions
const getPlanIcon = (planName: string) => {
  switch (planName?.toLowerCase()) {
    case "basic":
      return Zap;
    case "professional":
      return Crown;
    case "enterprise":
      return Star;
    case "premium":
      return Star;
    default:
      return Crown;
  }
};

const getPlanColor = (planName: string) => {
  switch (planName?.toLowerCase()) {
    case "basic":
      return "#f59e0b";
    case "professional":
      return "#3b82f6";
    case "enterprise":
      return "#10b981";
    case "premium":
      return "#8b5cf6";
    default:
      return "#f97316";
  }
};

const generateFeatures = (subscription: any) => {
  const features = [];
  if (subscription.isUnlimited) {
    features.push("Unlimited orders");
  } else if (subscription.orderLimit) {
    features.push(`Up to ${subscription.orderLimit} orders`);
  } else {
    features.push("Custom order limit");
  }
  features.push(`${subscription.validityInDays} days validity`);
  features.push("Full platform access");
  features.push("24/7 customer support");
  if (subscription.isFree) {
    features.push("Free plan features");
  } else {
    features.push("Premium features");
  }
  return features;
};

interface BusinessPlanValues {
  commissionRate: string;
  currentPlan: {
    type: "commission" | "subscription";
    planId?: string;
    planName?: string;
    planData?: any;
  };
}

const BusinessPlan = ({ state }) => {
  const { items: subscriptions } = useSelector(
    (state: RootState) => state.subscriptions,
  );
  const { data: singleStore, loading } = useSelector(
    (state: RootState) => state.singleStore,
  );

  const { showModal, hideModal } = useModal();
  const [isSubmitting, setIsSubmitting] = useState(false);

  const fetchSingleStore = useCallback(() => {
    if (state?.id) {
      StoreApi.find({ state: "singleStore", id: state.id });
    }
  }, [state?.id]);

  useEffect(() => {
    fetchSingleStore();
  }, [fetchSingleStore]);

  const [initialValues, setInitialValues] = useState<BusinessPlanValues>({
    commissionRate: state?.commission || "0",
    currentPlan: {
      type: state?.plan === "Subscription" ? "subscription" : "commission",
      planId: state?.subscription?.id?.toString(),
      planName: state?.subscription?.name,
      planData: state?.subscription,
    },
  });

  // Update initialValues when singleStore data changes
  useEffect(() => {
    if (singleStore && singleStore.id) {
      setInitialValues({
        commissionRate: singleStore.commission || "0",
        currentPlan: {
          type:
            singleStore.plan === "Subscription" ? "subscription" : "commission",
          planId: singleStore.subscription?.id?.toString(),
          planName: singleStore.subscription?.name,
          planData: singleStore.subscription,
        },
      });
    }
  }, [singleStore]);

  const fetchSubscriptions = useCallback(async () => {
    await Subscription.list({
      state: "subscriptions",
      params: { moduleId: state?.module?.id },
    });
  }, [state?.module?.id]);

  useEffect(() => {
    fetchSubscriptions();
  }, [fetchSubscriptions]);

  const handleSubmit = async (
    values: BusinessPlanValues,
    { setSubmitting }: any,
  ) => {
    setIsSubmitting(true);
    try {
      if (values.currentPlan.type === "commission") {
        const formData = new FormData();
        formData.append("plan", "Commission");
        formData.append("commission", values.commissionRate);

        await StoreApi.update({
          state: "stores",
          id: state.id,
          data: formData,
        });

        // Refetch to update Redux state
        fetchSingleStore();

        toast.success("Commission rate updated successfully!");
      } else {
        // For subscription, typically we don't update via this submit button unless managing other fields
        // But if there are fields, update them.
        toast.info("Subscription plan is active.");
      }
    } catch (error) {
      console.error("Error updating business plan:", error);
      toast.error("Error updating business plan");
    } finally {
      setIsSubmitting(false);
      setSubmitting(false);
    }
  };

  const handleSwitchToSubscription = (
    plan: any,
    currentPlan: any,
    setFieldValue: any,
  ) => {
    showModal(
      <PlanSwitchModal
        currentPlan={currentPlan}
        newPlan={plan}
        onConfirm={async (paymentMethod) => {
          try {
            const formData = new FormData();
            formData.append("plan", "Subscription");
            formData.append("subscriptionId", plan.id.toString());

            await StoreApi.update({
              state: "stores",
              id: state.id,
              data: formData,
            });

            // Refetch to update Redux state
            fetchSingleStore();

            hideModal();
            toast.success(`Successfully switched to ${plan.name} plan!`);
          } catch (e) {
            console.error(e);
            toast.error("Failed to switch plan");
          }
        }}
        onCancel={() => hideModal()}
      />,
      { width: "70%", height: "600px" },
    );
  };

  const handleSwitchToCommission = (currentPlan: any, setFieldValue: any) => {
    showModal(
      <SwitchToCommissionModal
        currentPlan={currentPlan}
        onConfirm={async () => {
          try {
            const formData = new FormData();
            formData.append("plan", "Commission");
            formData.append("commission", "5");

            await StoreApi.update({
              state: "stores",
              id: state.id,
              data: formData,
            });

            // Refetch to update Redux state
            fetchSingleStore();

            hideModal();
            toast.success("Successfully switched to commission-based pricing!");
          } catch (e) {
            console.error(e);
            toast.error("Failed to switch to commission");
          }
        }}
        onCancel={() => hideModal()}
      />,
      { width: "500px" },
    );
  };

  const handleCancelSubscription = (setFieldValue: any) => {
    showModal(
      <CancelSubscriptionModal
        onConfirm={async () => {
          try {
            const formData = new FormData();
            formData.append("plan", "Commission");
            formData.append("commission", "5");

            await StoreApi.update({
              state: "stores",
              id: state.id,
              data: formData,
            });

            // Refetch to update Redux state
            fetchSingleStore();

            hideModal();
            toast.success("Subscription cancelled successfully!");
          } catch (e) {
            console.error(e);
            toast.error("Failed to cancel subscription");
          }
        }}
        onCancel={() => hideModal()}
      />,
      { width: "500px" },
    );
  };

  const transformedSubscriptions =
    subscriptions?.map((subscription: any) => ({
      id: subscription.id.toString(),
      name: subscription.name,
      price: subscription.price,
      period: `for ${subscription.validityInDays} days`,
      description: subscription.content
        ? subscription.content.replace(/<[^>]*>/g, "").substring(0, 120) + "..."
        : `${subscription.name} plan`,
      icon: getPlanIcon(subscription.name),
      iconColor: getPlanColor(subscription.name),
      features: generateFeatures(subscription),
      isPopular: !subscription.isFree && !subscription.isDisabled,
      content: subscription.content,
      ...subscription,
    })) || [];

  // Find current plan to show it first
  const currentPlanId = initialValues.currentPlan.planId;
  const sortedSubscriptions = [...transformedSubscriptions].sort((a, b) => {
    if (a.id === currentPlanId) return -1;
    if (b.id === currentPlanId) return 1;
    return 0;
  });
  const { settings } = useGeneralSettings();

  if (loading) {
    return (
      <S.Container>
        <S.LoadingFormContainer>
          {/* Shimmer for Current Plan Section */}
          <S.Section>
            <S.SectionHeader>
              <S.ShimmerDiv width="48px" height="48px" borderRadius="12px" />
              <div style={{ flex: 1 }}>
                <S.ShimmerDiv width="200px" height="24px" marginBottom="8px" />
                <S.ShimmerDiv width="300px" height="16px" />
              </div>
            </S.SectionHeader>
            <S.ShimmerDiv width="100%" height="150px" borderRadius="8px" />
          </S.Section>

          {/* Shimmer for Available Plans Section */}
          <S.Section>
            <S.SectionHeader>
              <S.ShimmerDiv width="48px" height="48px" borderRadius="12px" />
              <div style={{ flex: 1 }}>
                <S.ShimmerDiv width="250px" height="24px" marginBottom="8px" />
                <S.ShimmerDiv width="400px" height="16px" />
              </div>
            </S.SectionHeader>

            <S.SubscriptionPlansContainer>
              {[1, 2, 3].map((i) => (
                <S.PlanCard
                  key={i}
                  style={{ padding: "24px", border: "2px solid #e2e8f0" }}
                >
                  <div
                    style={{
                      display: "flex",
                      justifyContent: "center",
                      marginBottom: "24px",
                    }}
                  >
                    <S.ShimmerDiv
                      width="64px"
                      height="64px"
                      borderRadius="16px"
                    />
                  </div>
                  <div
                    style={{
                      display: "flex",
                      flexDirection: "column",
                      alignItems: "center",
                      gap: "8px",
                      marginBottom: "24px",
                    }}
                  >
                    <S.ShimmerDiv width="120px" height="24px" />
                    <S.ShimmerDiv width="80px" height="32px" />
                    <S.ShimmerDiv width="100px" height="16px" />
                  </div>
                  <S.ShimmerDiv
                    width="100%"
                    height="60px"
                    marginBottom="20px"
                  />
                  <div
                    style={{
                      display: "flex",
                      flexDirection: "column",
                      gap: "12px",
                      marginBottom: "24px",
                    }}
                  >
                    <S.ShimmerDiv width="100%" height="16px" />
                    <S.ShimmerDiv width="100%" height="16px" />
                    <S.ShimmerDiv width="80%" height="16px" />
                  </div>
                  <S.ShimmerDiv width="100%" height="48px" borderRadius="8px" />
                </S.PlanCard>
              ))}
            </S.SubscriptionPlansContainer>
          </S.Section>
        </S.LoadingFormContainer>
      </S.Container>
    );
  }

  return (
    <S.Container>
      <Formik
        initialValues={initialValues}
        onSubmit={handleSubmit}
        enableReinitialize
      >
        {({ values, setFieldValue }) => (
          <S.FormContainer>
            {/* Current Plan Section - Always First */}
            <S.Section>
              <S.SectionHeader>
                <S.SectionIcon>
                  <DollarSign size={24} />
                </S.SectionIcon>
                <div>
                  <S.SectionTitle>
                    Current Business Plan
                    <S.CurrentPlanBadge type={values.currentPlan.type}>
                      {values.currentPlan.type === "commission" ? (
                        <>
                          <DollarSign size={14} />
                          Commission Based
                        </>
                      ) : (
                        <>
                          <Crown size={14} />
                          {values.currentPlan.planName}
                        </>
                      )}
                    </S.CurrentPlanBadge>
                  </S.SectionTitle>
                  <S.SectionDescription>
                    {values.currentPlan.type === "commission"
                      ? "You are currently on commission-based pricing. Switch to a subscription plan for fixed monthly pricing."
                      : `You are subscribed to the ${values.currentPlan.planName} plan. Manage your subscription settings.`}
                  </S.SectionDescription>
                </div>
              </S.SectionHeader>

              {/* Show Commission Settings ONLY when on commission-based plan */}
              {values.currentPlan.type === "commission" && (
                <CommissionSettings isActive={true} />
              )}

              {/* Show Subscription Info ONLY when on subscription plan */}
              {values.currentPlan.type === "subscription" &&
                values.currentPlan.planData && (
                  <div>
                    <div
                      style={{
                        background: "#f8fafc",
                        padding: "24px",
                        borderRadius: "12px",
                        border: `1px solid #e2e8f0`,
                      }}
                    >
                      <div
                        style={{
                          display: "flex",
                          justifyContent: "space-between",
                          alignItems: "flex-start",
                          marginBottom: "16px",
                        }}
                      >
                        <div>
                          <h4
                            style={{
                              margin: "0 0 8px 0",
                              color: "#1e293b",
                              fontSize: "18px",
                              fontWeight: "700",
                            }}
                          >
                            Active Subscription: {values.currentPlan.planName}
                          </h4>
                          <p
                            style={{
                              margin: "0 0 12px 0",
                              color: "#475569",
                              fontSize: "14px",
                              lineHeight: "1.5",
                            }}
                          >
                            You are on a subscription plan. Your commission rate
                            is disabled while subscription is active.
                          </p>
                        </div>
                        <S.ActionButtonsContainer>
                          <S.SwitchToCommissionButton
                            onClick={() =>
                              handleSwitchToCommission(
                                values.currentPlan,
                                setFieldValue,
                              )
                            }
                          >
                            <DollarSign size={16} />
                            Switch to Commission
                          </S.SwitchToCommissionButton>
                          <S.CancelSubscriptionButton
                            onClick={() =>
                              handleCancelSubscription(setFieldValue)
                            }
                          >
                            <X size={16} />
                            Cancel Subscription
                          </S.CancelSubscriptionButton>
                        </S.ActionButtonsContainer>
                      </div>

                      {/* Enhanced Subscription Details */}
                      <S.SubscriptionDetails>
                        <S.DetailRow>
                          <S.DetailIcon>
                            <Calendar size={20} />
                          </S.DetailIcon>
                          <S.DetailText>
                            <S.DetailLabel>Plan Duration</S.DetailLabel>
                            <S.DetailValue>
                              {values.currentPlan.planData.validityInDays} days
                            </S.DetailValue>
                          </S.DetailText>
                        </S.DetailRow>
                        <S.DetailRow>
                          <S.DetailIcon>
                            <DollarSign size={20} />
                          </S.DetailIcon>
                          <S.DetailText>
                            <S.DetailLabel>Price</S.DetailLabel>
                            <S.DetailValue>
                              {Helper.formatCurrency(
                                values.currentPlan.planData.price,
                                settings,
                              )}
                            </S.DetailValue>
                          </S.DetailText>
                        </S.DetailRow>
                        <S.DetailRow>
                          <S.DetailIcon>
                            {values.currentPlan.planData.isUnlimited ? (
                              <Infinity size={20} />
                            ) : (
                              <Target size={20} />
                            )}
                          </S.DetailIcon>
                          <S.DetailText>
                            <S.DetailLabel>Order Limit</S.DetailLabel>
                            <S.DetailValue>
                              {values.currentPlan.planData.isUnlimited
                                ? "Unlimited"
                                : values.currentPlan.planData.orderLimit
                                  ? `${values.currentPlan.planData.orderLimit} orders`
                                  : "Custom limit"}
                            </S.DetailValue>
                          </S.DetailText>
                        </S.DetailRow>
                        <S.DetailRow>
                          <S.DetailIcon>
                            <Shield size={20} />
                          </S.DetailIcon>
                          <S.DetailText>
                            <S.DetailLabel>Plan Type</S.DetailLabel>
                            <S.DetailValue>
                              {values.currentPlan.planData.isFree
                                ? "Free"
                                : "Premium"}
                              {values.currentPlan.planData.isDisabled &&
                                " (Disabled)"}
                            </S.DetailValue>
                          </S.DetailText>
                        </S.DetailRow>
                      </S.SubscriptionDetails>

                      {/* Plan Content Display */}
                      {values.currentPlan.planData.content && (
                        <S.PlanContentSection>
                          <S.PlanContentTitle>
                            <Sparkles size={18} />
                            Plan Details
                          </S.PlanContentTitle>
                          <S.PlanContentDisplay
                            dangerouslySetInnerHTML={{
                              __html: values.currentPlan.planData.content,
                            }}
                          />
                        </S.PlanContentSection>
                      )}
                    </div>
                  </div>
                )}
            </S.Section>

            {/* Available Plans Section */}
            <S.Section>
              <S.SectionHeader>
                <S.SectionIcon>
                  <Crown size={24} />
                </S.SectionIcon>
                <div>
                  <S.SectionTitle>Available Subscription Plans</S.SectionTitle>
                  <S.SectionDescription>
                    Choose a subscription plan that fits your business needs.
                    All plans include full platform access.
                  </S.SectionDescription>
                </div>
              </S.SectionHeader>

              <S.SubscriptionPlansContainer>
                {sortedSubscriptions.map((plan) => {
                  const Icon = plan.icon;
                  const isCurrentPlan =
                    values.currentPlan.type === "subscription" &&
                    values.currentPlan.planId === plan.id;

                  return (
                    <S.SubscriptionPlanCard
                      key={plan.id}
                      isPopular={plan.isPopular}
                      isCurrent={isCurrentPlan}
                    >
                      {plan.isPopular && (
                        <S.PopularBadge>Most Popular</S.PopularBadge>
                      )}
                      {isCurrentPlan && (
                        <S.CurrentPlanBadgeCard>
                          Current Plan
                        </S.CurrentPlanBadgeCard>
                      )}

                      <S.PlanHeaderCard>
                        <S.PlanIconCard color={plan.iconColor}>
                          <Icon size={28} />
                        </S.PlanIconCard>
                        <S.PlanName>{plan.name}</S.PlanName>
                        <S.PlanPrice>
                          {Helper.formatCurrency(plan.price, settings)}
                        </S.PlanPrice>
                        <S.PlanPeriod>{plan.period}</S.PlanPeriod>
                      </S.PlanHeaderCard>

                      <S.PlanDescription>{plan.description}</S.PlanDescription>

                      <S.FeaturesList>
                        {plan.features.map((feature, index) => (
                          <S.FeatureItem key={index}>
                            <Check size={16} color="#10b981" />
                            {feature}
                          </S.FeatureItem>
                        ))}
                      </S.FeaturesList>

                      <S.SelectButton
                        type="button"
                        variant={
                          isCurrentPlan
                            ? "current"
                            : values.currentPlan.type === "commission"
                              ? "primary"
                              : "secondary"
                        }
                        onClick={() =>
                          handleSwitchToSubscription(
                            plan,
                            values.currentPlan,
                            setFieldValue,
                          )
                        }
                        disabled={isCurrentPlan || plan.isDisabled}
                      >
                        {isCurrentPlan
                          ? "Current Plan"
                          : plan.isDisabled
                            ? "Disabled"
                            : values.currentPlan.type === "commission"
                              ? "Select Plan"
                              : "Switch to Plan"}
                      </S.SelectButton>
                    </S.SubscriptionPlanCard>
                  );
                })}
              </S.SubscriptionPlansContainer>
            </S.Section>
          </S.FormContainer>
        )}
      </Formik>
    </S.Container>
  );
};

// Enhanced Plan Switch Modal Component
const PlanSwitchModal: React.FC<{
  currentPlan: any;
  newPlan: any;
  onConfirm: (paymentMethod: string) => void;
  onCancel: () => void;
}> = ({ currentPlan, newPlan, onConfirm, onCancel }) => {
  const [selectedPayment, setSelectedPayment] = useState("online");

  const paymentMethods = [
    {
      id: "online",
      name: "Online Payment",
      description:
        "Pay securely with credit card, debit card, or digital wallet",
      icon: CreditCard,
      color: "#3b82f6",
    },
    {
      id: "cash",
      name: "Cash Payment",
      description: "Pay with cash at our nearest office location",
      icon: Wallet,
      color: "#10b981",
    },
  ];
  const { settings, existing, setSettings, loading } = useGeneralSettings();

  return (
    <S.ModalContainer>
      <S.ModalHeader>
        <S.ModalTitle>Upgrade Your Plan</S.ModalTitle>
        <S.ModalSubtitle>
          Switch to {newPlan.name} and unlock premium features
        </S.ModalSubtitle>
      </S.ModalHeader>

      <S.ModalContent>
        {/* Plan Comparison */}
        <S.PlanComparison>
          <S.CurrentPlanCard>
            <S.PlanCardTitle>Current Plan</S.PlanCardTitle>
            <S.PlanCardName>
              {currentPlan.type === "commission"
                ? "Commission Based"
                : currentPlan.planName}
            </S.PlanCardName>
            <S.PlanCardPrice>
              {currentPlan.type === "commission"
                ? "Variable"
                : `${Helper.formatCurrency(currentPlan.planData?.price, settings)}`}
            </S.PlanCardPrice>
            <S.PlanCardPeriod>
              {currentPlan.type === "commission"
                ? "Per order commission"
                : `${currentPlan.planData?.validityInDays || 0} days`}
            </S.PlanCardPeriod>
          </S.CurrentPlanCard>

          <S.ArrowContainer>
            <ArrowRight size={28} />
          </S.ArrowContainer>

          <S.NewPlanCard>
            <S.PlanCardTitle>New Plan</S.PlanCardTitle>
            <S.PlanCardName>{newPlan.name}</S.PlanCardName>
            <S.PlanCardPrice>
              {Helper.formatCurrency(newPlan.price, settings)}
            </S.PlanCardPrice>
            <S.PlanCardPeriod>{newPlan.period}</S.PlanCardPeriod>
          </S.NewPlanCard>
        </S.PlanComparison>

        {/* Dynamic Content Display */}
        {newPlan.content && newPlan.content.replace(/<[^>]*>/g, "").trim() && (
          <S.ContentSection>
            <S.ContentTitle>
              <Sparkles size={24} />
              Plan Details
            </S.ContentTitle>
            <S.ContentDisplay
              dangerouslySetInnerHTML={{ __html: newPlan.content }}
            />
          </S.ContentSection>
        )}

        {/* Payment Methods */}
        <S.PaymentSection>
          <S.PaymentTitle>
            <CreditCard size={24} />
            Select Payment Method
          </S.PaymentTitle>
          <S.PaymentMethods>
            {paymentMethods.map((method) => {
              const Icon = method.icon;
              return (
                <S.PaymentMethod
                  key={method.id}
                  className={selectedPayment === method.id ? "selected" : ""}
                  onClick={() => setSelectedPayment(method.id)}
                >
                  <S.PaymentIcon>
                    <Icon size={24} />
                  </S.PaymentIcon>
                  <S.PaymentInfo>
                    <S.PaymentName>{method.name}</S.PaymentName>
                    <S.PaymentDescription>
                      {method.description}
                    </S.PaymentDescription>
                  </S.PaymentInfo>
                  <input
                    type="radio"
                    name="paymentMethod"
                    value={method.id}
                    checked={selectedPayment === method.id}
                    onChange={() => setSelectedPayment(method.id)}
                    style={{ display: "none" }}
                  />
                </S.PaymentMethod>
              );
            })}
          </S.PaymentMethods>
        </S.PaymentSection>

        {/* Action Buttons */}
        <S.ModalActions>
          <S.ModalButton variant="secondary" onClick={onCancel}>
            <X size={18} />
            Cancel
          </S.ModalButton>
          <S.ModalButton
            variant="primary"
            onClick={() => onConfirm(selectedPayment)}
          >
            <TrendingUp size={18} />
            Upgrade Plan
          </S.ModalButton>
        </S.ModalActions>
      </S.ModalContent>
    </S.ModalContainer>
  );
};

// Switch to Commission Modal Component
const SwitchToCommissionModal: React.FC<{
  currentPlan: any;
  onConfirm: () => void;
  onCancel: () => void;
}> = ({ currentPlan, onConfirm, onCancel }) => {
  return (
    <S.ModalContainer style={{ maxWidth: "500px" }}>
      <S.ModalHeader>
        <S.ModalTitle>Switch to Commission</S.ModalTitle>
        <S.ModalSubtitle>
          Change your pricing model to commission-based
        </S.ModalSubtitle>
      </S.ModalHeader>

      <S.ModalContent>
        <S.ConfirmationModalContent>
          <S.ConfirmationIcon>
            <DollarSign size={32} />
          </S.ConfirmationIcon>

          <S.ConfirmationTitle>
            Switch to Commission-Based Pricing?
          </S.ConfirmationTitle>

          <S.ConfirmationDescription>
            You are about to switch from your current subscription plan to
            commission-based pricing. This change will take effect immediately.
          </S.ConfirmationDescription>

          <S.ConfirmationDetails>
            <S.ConfirmationDetailRow>
              <S.ConfirmationDetailLabel>
                Current Plan:
              </S.ConfirmationDetailLabel>
              <S.ConfirmationDetailValue>
                {currentPlan.planName}
              </S.ConfirmationDetailValue>
            </S.ConfirmationDetailRow>
            <S.ConfirmationDetailRow>
              <S.ConfirmationDetailLabel>New Model:</S.ConfirmationDetailLabel>
              <S.ConfirmationDetailValue>
                Commission-Based
              </S.ConfirmationDetailValue>
            </S.ConfirmationDetailRow>
            <S.ConfirmationDetailRow>
              <S.ConfirmationDetailLabel>Effective:</S.ConfirmationDetailLabel>
              <S.ConfirmationDetailValue>Immediately</S.ConfirmationDetailValue>
            </S.ConfirmationDetailRow>
          </S.ConfirmationDetails>

          <S.ModalActions>
            <S.ModalButton variant="secondary" onClick={onCancel}>
              <X size={18} />
              Cancel
            </S.ModalButton>
            <S.ModalButton variant="primary" onClick={onConfirm}>
              <DollarSign size={18} />
              Switch to Commission
            </S.ModalButton>
          </S.ModalActions>
        </S.ConfirmationModalContent>
      </S.ModalContent>
    </S.ModalContainer>
  );
};

// Cancel Subscription Modal Component
const CancelSubscriptionModal: React.FC<{
  onConfirm: () => void;
  onCancel: () => void;
}> = ({ onConfirm, onCancel }) => {
  return (
    <S.ModalContainer style={{ maxWidth: "500px" }}>
      <S.ModalHeader
        style={{
          background: "linear-gradient(135deg, #ef4444 0%, #dc2626 100%)",
        }}
      >
        <S.ModalTitle>Cancel Subscription</S.ModalTitle>
        <S.ModalSubtitle>
          Are you sure you want to cancel your subscription?
        </S.ModalSubtitle>
      </S.ModalHeader>

      <S.ModalContent>
        <S.ConfirmationModalContent>
          <S.ConfirmationIcon
            style={{ background: "#ef444415", color: "#ef4444" }}
          >
            <AlertTriangle size={32} />
          </S.ConfirmationIcon>

          <S.ConfirmationTitle>Cancel Your Subscription?</S.ConfirmationTitle>

          <S.ConfirmationDescription>
            This will cancel your current subscription and switch you back to
            commission-based pricing. You will lose access to subscription
            benefits immediately.
          </S.ConfirmationDescription>

          <S.ConfirmationDetails>
            <S.ConfirmationDetailRow>
              <S.ConfirmationDetailLabel>Action:</S.ConfirmationDetailLabel>
              <S.ConfirmationDetailValue>
                Cancel Subscription
              </S.ConfirmationDetailValue>
            </S.ConfirmationDetailRow>
            <S.ConfirmationDetailRow>
              <S.ConfirmationDetailLabel>New Model:</S.ConfirmationDetailLabel>
              <S.ConfirmationDetailValue>
                Commission-Based
              </S.ConfirmationDetailValue>
            </S.ConfirmationDetailRow>
            <S.ConfirmationDetailRow>
              <S.ConfirmationDetailLabel>Effective:</S.ConfirmationDetailLabel>
              <S.ConfirmationDetailValue>Immediately</S.ConfirmationDetailValue>
            </S.ConfirmationDetailRow>
          </S.ConfirmationDetails>

          <S.ModalActions>
            <S.ModalButton variant="secondary" onClick={onCancel}>
              <X size={18} />
              Keep Subscription
            </S.ModalButton>
            <S.ModalButton
              variant="primary"
              onClick={onConfirm}
              style={{
                background: "linear-gradient(135deg, #ef4444 0%, #dc2626 100%)",
                boxShadow: "0 4px 15px rgba(239, 68, 68, 0.3)",
              }}
            >
              <X size={18} />
              Cancel Subscription
            </S.ModalButton>
          </S.ModalActions>
        </S.ConfirmationModalContent>
      </S.ModalContent>
    </S.ModalContainer>
  );
};

export default BusinessPlan;
